/**
 * 표준화된 API 에러 핸들러
 *
 * 모든 API 서비스에서 일관된 에러 처리를 위한 유틸리티
 */
import { apiFetch } from '../utils/apiFetch';

/**
 * API 에러 클래스
 */
export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public detail?: string
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * API 응답 에러 타입
 */
interface ApiErrorResponse {
  detail?: string;
  message?: string;
}

/**
 * 표준화된 fetch 에러 핸들러
 *
 * @param response - fetch Response 객체
 * @param context - 에러 컨텍스트 (로깅용)
 * @throws ApiError
 */
export async function handleApiError(
  response: Response,
  context: string = 'API 호출'
): Promise<never> {
  let errorData: ApiErrorResponse = {};

  try {
    errorData = await response.json();
  } catch (e) {
    console.error('Error parsing API error response:', e);
  }

  const message =
    errorData.detail ||
    errorData.message ||
    `${context} 실패: ${response.status} ${response.statusText}`;

  console.error(`[API Error] ${context}:`, {
    status: response.status,
    statusText: response.statusText,
    detail: errorData.detail,
    message: errorData.message,
  });

  throw new ApiError(message, response.status, errorData.detail);
}

/**
 * 표준화된 fetch 래퍼 (X-API-Key 인증 헤더 자동 포함)
 *
 * @param url - 요청 URL
 * @param options - fetch options
 * @param context - 에러 컨텍스트
 * @returns Response 객체
 * @throws ApiError
 */
export async function fetchWithErrorHandling(
  url: string,
  options?: RequestInit,
  context?: string
): Promise<Response> {
  try {
    const response = await apiFetch(url, options);

    if (!response.ok) {
      await handleApiError(response, context);
    }

    return response;
  } catch (error) {
    if (error instanceof ApiError) {
      throw error;
    }

    console.error(`[Network Error] ${context || 'API 호출'}:`, error);
    throw new ApiError(
      `네트워크 오류: ${error instanceof Error ? error.message : '알 수 없는 오류'}`,
      0
    );
  }
}

/**
 * JSON 응답 fetch 래퍼
 *
 * @param url - 요청 URL
 * @param options - fetch options
 * @param context - 에러 컨텍스트
 * @returns JSON 파싱된 데이터
 * @throws ApiError
 */
export async function fetchJson<T>(
  url: string,
  options?: RequestInit,
  context?: string
): Promise<T> {
  const response = await fetchWithErrorHandling(url, options, context);

  try {
    return await response.json();
  } catch (error) {
    console.error('Error parsing JSON response:', error);
    throw new ApiError('응답 데이터 파싱 실패', response.status);
  }
}

/**
 * 사용자 친화적 에러 메시지 생성
 *
 * @param error - ApiError 또는 Error
 * @returns 사용자에게 표시할 메시지
 */
export function getUserFriendlyErrorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    switch (error.status) {
      case 0:
        return '서버에 연결할 수 없습니다. 네트워크 연결을 확인하세요.';
      case 400:
        return error.detail || '잘못된 요청입니다.';
      case 401:
        return 'API 인증이 필요합니다. 설정에서 API 키를 확인하세요.';
      case 403:
        return '접근 권한이 없습니다.';
      case 404:
        return '요청한 데이터를 찾을 수 없습니다.';
      case 429:
        return 'API 호출 한도를 초과했습니다. 잠시 후 다시 시도하세요.';
      case 500:
        return '서버 오류가 발생했습니다.';
      case 502:
        return '거래소 API 오류가 발생했습니다.';
      case 503:
        return '서비스를 일시적으로 사용할 수 없습니다.';
      default:
        return error.message;
    }
  }

  if (error instanceof Error) {
    return error.message;
  }

  return '알 수 없는 오류가 발생했습니다.';
}

/**
 * 에러 로깅 유틸리티
 *
 * @param context - 에러 발생 위치
 * @param error - 에러 객체
 */
export function logApiError(context: string, error: unknown): void {
  console.error(`[${context}]`, {
    error,
    type: error instanceof ApiError ? 'ApiError' : 'Error',
    status: error instanceof ApiError ? error.status : 'N/A',
    message: error instanceof Error ? error.message : String(error),
  });
}
